import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { createQuoteScheduler } from "@/modules/dashboard/crypto-swaps/utils/cryptoSwapQuoteScheduler";
import { useQuoteExpiry } from "@/shared/hooks/useQuoteExpiry";
import { normalizePaymentError } from "@/shared/services/payments/paymentErrors";

import {
  type NativeQuote,
  type NativeQuoteRequest,
  PaymentsQuotesService,
} from "../services/paymentsQuotes.service";
import { type SendFeeMode, type SendRoute, sendQuoteRequest } from "../utils/walletSend";

const QUOTE_DEBOUNCE_MS = 500;

interface QuoteJob {
  request: NativeQuoteRequest;
  signature: string;
}

interface SettledQuote {
  signature: string;
  quote: NativeQuote | null;
  error: string | null;
  errorCode: string | null;
  retryable: boolean;
}

export interface UseWalletSendQuoteParams {
  /**
   * Decimal string in the currency the fee mode fixes (`amountCurrencyFor`), or null while nothing usable is
   * typed.
   */
  amount: string | null;
  feeMode: SendFeeMode;
  route: SendRoute;
  /** INTERNAL staff only; members are pinned server-side and must not send it. */
  organizationId?: string;
  enabled: boolean;
}

export interface WalletSendQuoteState {
  /** Prices exactly what is on screen right now; null while re-pricing. */
  quote: NativeQuote | null;
  /** The last answer, whatever it priced — for a dimmed figure while the next one arrives. */
  lastQuote: NativeQuote | null;
  loading: boolean;
  error: string | null;
  errorCode: string | null;
  /** The request got no answer or the server failed: asking again may work. Otherwise the amount is the issue. */
  errorRetryable: boolean;
  refresh: () => void;
}

/**
 * Debounced on typing, immediate when only the fee mode or the route moved. An expired quote is re-priced
 * silently: a same-currency one only froze the commission, and a cross-currency one shows its countdown on
 * screen, so in both cases the screen says so only if the figures move.
 */
export function useWalletSendQuote({
  amount,
  feeMode,
  route,
  organizationId,
  enabled,
}: UseWalletSendQuoteParams): WalletSendQuoteState {
  const [tick, setTick] = useState(0);
  const [settled, setSettled] = useState<SettledQuote | null>(null);

  const { originCurrency, country, currency, rail } = route;
  const routeKey = `${originCurrency}>${country}:${currency}:${rail}`;
  const signature =
    enabled && amount ? `${amount}|${feeMode}|${routeKey}|${organizationId ?? ""}|${tick}` : null;
  const request = useMemo(
    () =>
      signature && amount
        ? sendQuoteRequest({
            route: { originCurrency, country, currency, rail },
            amount,
            feeMode,
            organizationId,
          })
        : null,
    [signature, amount, feeMode, originCurrency, country, currency, rail, organizationId],
  );

  const scheduler = useMemo(
    () =>
      createQuoteScheduler<QuoteJob, NativeQuote>({
        delayMs: QUOTE_DEBOUNCE_MS,
        run: async ({ request: body }) => (await PaymentsQuotesService.createQuote(body)).data.quote,
        onResult: (quote, job) =>
          setSettled({ signature: job.signature, quote, error: null, errorCode: null, retryable: false }),
        onError: (error, job) => {
          const normalized = normalizePaymentError(error);
          setSettled({
            signature: job.signature,
            quote: null,
            error: normalized.message || null,
            errorCode: normalized.code,
            retryable:
              normalized.kind === "network" ||
              normalized.kind === "unknown" ||
              (normalized.status ?? 0) >= 500,
          });
        },
      }),
    [],
  );

  const pricedAmount = useRef<string | null>(null);
  useEffect(() => {
    if (!signature || !request) {
      scheduler.cancel();
      pricedAmount.current = null;
      return;
    }
    const immediate = pricedAmount.current === amount;
    pricedAmount.current = amount;
    scheduler.schedule({ request, signature }, immediate);
  }, [signature, request, amount, scheduler]);

  useEffect(() => () => scheduler.cancel(), [scheduler]);

  const current = signature && settled?.signature === signature ? settled : null;
  const quote = current?.quote ?? null;

  const refresh = useCallback(() => setTick((value) => value + 1), []);
  useQuoteExpiry(quote?.expires_at ?? null, refresh);

  return {
    quote,
    lastQuote: settled?.quote ?? null,
    loading: !!signature && !current,
    error: current?.error ?? null,
    errorCode: current?.errorCode ?? null,
    errorRetryable: current?.retryable ?? false,
    refresh,
  };
}
